/**
 * PendingActionCard — inline two-step confirmation card for AI write actions.
 *
 * Extracted from AIAssistant.jsx (sprint F2). Stateless: receives the action
 * (type + preview fields), its current status (pending/approved/rejected),
 * and approve/reject handlers from the parent.
 *
 * Sprint post-E2E 2026-05-25 (LENGUAJE HUMANO): defensa en profundidad
 * contra tecnicismos visibles. El backend ya sanea el preview en
 * verix-api/src/functions/ai/v2/previewSanitizer.ts, pero aqui hacemos
 * filtros espejo (UUIDs, claves de BD, claves de identificador) por si
 * algun handler nuevo escapa al saneo central.
 */

import { Check, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ACTION_REGISTRY } from './actions/registry';
import AriaImpactBlock from './AriaImpactBlock';
import OverrideForceControl from './OverrideForceControl';
import { useApp } from '@/components/hooks/useApp';
import {
  ACTION_LABELS,
  humanizeActionType,
  humanizeKey,
  shouldHideKey,
  isUuidValue,
  formatPreviewValue,
} from './previewSanitizer';

export { ACTION_LABELS };

// #6 flujo A — roles que el backend permite forzar (owner/accountant; admin es
// superset). Gate solo UX: el backend re-valida el rol y audita el override.
const FORCE_ROLES = new Set(['owner', 'accountant', 'admin']);

/**
 * Fila para un valor anidado del preview (un objeto o una lista).
 *
 * Solo baja UN nivel a propósito: más profundidad convierte la tarjeta en un
 * volcado de JSON, y para eso ya están las tarjetas tipadas del registry. Se
 * aplican los mismos filtros que en el nivel de arriba (claves internas, UUIDs)
 * porque un anidado puede traer ids igual que la raíz.
 *
 * Las listas se resumen: si son de escalares se enumeran, y si son de objetos
 * se dice cuántos elementos hay. Enumerar objetos aquí sería ilegible y es
 * justo el caso que una tarjeta tipada resuelve mejor (líneas de un asiento,
 * de una factura…).
 */
function NestedPreviewRow({ label, value }) {
  if (Array.isArray(value)) {
    const escalares = value.filter((v) => v === null || typeof v !== 'object');
    const texto = escalares.length === value.length && value.length > 0
      ? escalares.map((v) => formatPreviewValue(v, label)).join(' · ')
      : `${value.length} elemento${value.length === 1 ? '' : 's'}`;
    return (
      <div className="flex justify-between">
        <span className="text-muted-foreground">{label}:</span>
        <span className="font-medium">{texto}</span>
      </div>
    );
  }

  const filas = Object.entries(value).filter(([k, v]) => {
    if (k === 'impact' || k.startsWith('_')) return false;
    if (shouldHideKey(k)) return false;
    if (isUuidValue(v)) return false;
    return v === null || typeof v !== 'object';
  });
  if (filas.length === 0) return null;

  return (
    <div>
      <div className="text-muted-foreground">{label}:</div>
      <div className="pl-3 space-y-0.5">
        {filas.map(([k, v]) => {
          const sub = humanizeKey(k);
          return (
            <div key={k} className="flex justify-between">
              <span className="text-muted-foreground">{sub}:</span>
              <span className="font-medium">{formatPreviewValue(v, sub)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PendingActionCard({ action, onApprove, onReject, status }) {
  const { user } = useApp();
  const isPending = status !== 'approved' && status !== 'rejected';
  const overrideReq = action.preview?._override_required;
  const canForce = FORCE_ROLES.has(user?.role);
  // Forzar → reenvía el confirm con override:{reason, scope}. editedMessage=null
  // deja el "Sí, confirma" por defecto; el binding por scope lo hace el backend.
  const forceOverride = (reason) => onApprove?.(null, { reason, scope: action.preview?._override_scope });
  const overrideControl = isPending && overrideReq?.length ? (
    <OverrideForceControl violations={overrideReq} canForce={canForce} onForce={forceOverride} />
  ) : null;

  // Delegate to a rich type-specific card if one is registered.
  const cards = ACTION_REGISTRY[action.type];
  if (cards?.PreviewCard) {
    // Las tarjetas tipadas leen claves crudas (preview.cliente, preview.lineas…).
    // El backend renombra las claves del preview a labels ES (para el card
    // genérico de abajo) pero adjunta `preview._raw` con las claves crudas.
    // Pasamos `_raw` (más `_humanRef`) a la tarjeta tipada; si no viene (backend
    // antiguo), cae al preview tal cual (retrocompatible).
    const raw = action.preview?._raw;
    const cardAction = raw
      ? { ...action, preview: { ...raw, _humanRef: action.preview?._humanRef } }
      : action;
    // #6 Fase 3b — the simulated-twin impact + invariant warnings live on the
    // OUTER preview (not in _raw), so render the shared block alongside the typed
    // card. Only while pending (the typed card shows its own approved/rejected
    // banner). No-op when the backend attaches no impact/warnings.
    const showImpact = status !== 'approved' && status !== 'rejected';
    return (
      <>
        <cards.PreviewCard action={cardAction} onApprove={onApprove} onReject={onReject} status={status} />
        {showImpact && <AriaImpactBlock preview={action.preview} />}
        {overrideControl}
      </>
    );
  }

  const label = humanizeActionType(action.type);

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <Check className="w-4 h-4" />
        <span>{label} aprobado</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-4 h-4" />
        <span>{label} cancelado</span>
      </div>
    );
  }

  // _humanRef viene del backend como identificacion humana canonica
  // (ej: "F-0061 — Naturgy Energy S.A. — 145,20 €"). Se renderiza como
  // subheader del card en lugar de mezclarse con las filas key/value.
  const humanRef = action.preview?._humanRef;

  return (
    <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg my-2">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className="text-warning border-warning/30 text-xs">
          Requiere aprobación
        </Badge>
        <span className="text-xs font-medium text-warning">{label}</span>
      </div>
      {humanRef && (
        <div className="text-xs font-semibold text-warning mb-2">
          {humanRef}
        </div>
      )}
      {action.preview && (
        <div className="space-y-1 text-xs mb-3">
          {Object.entries(action.preview).map(([key, val]) => {
            // Defensa en profundidad — filtrar claves tecnicas que el backend
            // ya deberia haber saneado. Si llega algo aqui, lo bloqueamos.
            // Las claves internas con prefijo `_` (y `impact`) las renderiza
            // AriaImpactBlock, no la lista key/value.
            if (key === 'impact' || key.startsWith('_')) return null;
            if (shouldHideKey(key)) return null;
            if (isUuidValue(val)) return null;
            // Un campo sin valor no informa de nada y ensucia la decisión: la
            // tarjeta de `crear_cliente` pintaba "Ciudad:" y "Teléfono:" en
            // blanco porque la tool manda la forma completa de la entidad.
            // `0` y `false` se conservan: son valores, no ausencias.
            if (val === null || val === undefined || val === '') return null;
            const friendlyLabel = humanizeKey(key);
            // Un valor anidado ya NO se descarta. Antes se hacía `return null`
            // y el usuario aprobaba sin ver justo lo que la tool había puesto
            // ahí para que lo viera: los permisos que concede una conexión
            // bancaria, el "posible duplicado" del OCR, el asiento que se va a
            // extornar o el contrato vigente que queda sustituido (auditoría
            // 13-08: 6 actionType sin tarjeta propia lo sufrían).
            if (val !== null && typeof val === 'object') {
              return <NestedPreviewRow key={key} label={friendlyLabel} value={val} />;
            }
            return (
              <div key={key} className="flex justify-between">
                <span className="text-muted-foreground">{friendlyLabel}:</span>
                <span className="font-medium">{formatPreviewValue(val, friendlyLabel)}</span>
              </div>
            );
          })}
        </div>
      )}
      <AriaImpactBlock preview={action.preview} />
      {overrideControl}
      <div className="flex gap-2">
        <Button size="sm" onClick={onApprove} className="flex-1 bg-success hover:bg-success/90 h-7 text-xs">
          <Check className="w-3 h-3 mr-1" /> Aprobar
        </Button>
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" /> Rechazar
        </Button>
      </div>
    </div>
  );
}
